/**
 * Types for the off-chain TP/SL trigger executor, reached through the backend
 * proxy at `/tpsl/*` (the proxy forwards verbatim to the executor).
 */

export type TpslTriggerType = "take_profit" | "stop_loss";

export type TpslMarginMode = "isolated" | "cross";

/**
 * Which price feed the executor watches to decide the trigger has fired.
 * `oracle` is the index/oracle price (the default, and what the executor used
 * before the field existed); `last` is the market's last traded price.
 */
export type TpslPriceSource = "oracle" | "last";

/** uint8 encoding used inside the EIP-712 payloads. */
export const TPSL_TRIGGER_TYPE_UINT8: Record<TpslTriggerType, number> = {
  take_profit: 0,
  stop_loss: 1,
};

/** uint8 encoding used inside the EIP-712 payloads. */
export const TPSL_PRICE_SOURCE_UINT8: Record<TpslPriceSource, number> = {
  oracle: 0,
  last: 1,
};

export const DEFAULT_TPSL_PRICE_SOURCE: TpslPriceSource = "oracle";

/** uint8 encoding used inside the EIP-712 payloads. */
export const TPSL_MARGIN_MODE_UINT8: Record<TpslMarginMode, number> = {
  isolated: 0,
  cross: 1,
};

/**
 * A single trigger leg.
 *
 * `triggerPrice` and `limitPrice` MUST be human decimal strings that carry a
 * decimal point (e.g. `"64000.0"`) — the executor's parser treats a bare
 * integer string as WEI. `sizeInTokensToClose` is the mirror image: it is sent
 * as the bare fixed-18 INTEGER (i.e. wei), because that is what the EIP-712
 * leaf hashes verbatim; `"0"` means "close the whole position at trigger time".
 *
 * `priceSource` is always sent (leaf v2). `limitPrice` is omitted entirely when
 * the user did not ask for an execution bound, and hashes as 0 in that case.
 */
export interface TpslLeg {
  triggerType: TpslTriggerType;
  triggerPrice: string;
  sizeInTokensToClose: string;
  slippageBps: number;
  priceSource: TpslPriceSource;
  limitPrice?: string;
}

export interface TpslAuth {
  kind: "eip712";
  nonce: number;
  deadline: number;
  signature: string;
}

export interface TpslCreateBody {
  trader: string;
  market: string;
  isLong: boolean;
  marginMode: TpslMarginMode;
  ocoLinked: boolean;
  orders: TpslLeg[];
  auth: TpslAuth;
}

export interface TpslCancelBody {
  trader: string;
  auth: TpslAuth;
}

/** A row as returned by `GET /tpsl/orders`. Extra executor fields are tolerated. */
export interface TpslOrder {
  id: string;
  market: string;
  symbol?: string;
  triggerType: TpslTriggerType;
  comparison?: string;
  triggerPrice: string;
  isLong: boolean;
  marginMode: TpslMarginMode;
  sizeInTokensToClose?: string;
  slippageBps?: number;
  /** Present since leaf v2; absent on rows created by the old client. */
  priceSource?: TpslPriceSource;
  limitPrice?: string | null;
  ocoGroupId?: string | null;
  status?: string;
  [key: string]: unknown;
}

/**
 * What the modal asks for: the desired end state of a position's triggers.
 *
 * All the optional advanced fields are "unset means keep the old behaviour":
 * no `sizeInTokens` -> full close, no limit price -> pure market close at
 * trigger time, no `priceSource` -> oracle.
 */
export interface TpslDesiredState {
  takeProfitPrice?: number;
  stopLossPrice?: number;
  /**
   * Human decimal size in the position's base asset; applies to BOTH legs.
   * Plain decimal text keeps an 18-decimal size exact (a number can round it).
   */
  sizeInTokens?: number | string;
  /** Applies to BOTH legs. */
  priceSource?: TpslPriceSource;
  takeProfitLimitPrice?: number;
  stopLossLimitPrice?: number;
}
